/*
  Week choices for the Reports page. Weeks run Monday to Saturday, the same
  calendar week the weekly Digest uses, and are named the way a manager thinks
  about them: how long ago, then the dates, then the week number.

    This week  · 5 Oct – 10 Oct 2026 · Wk 41
    Last week  · 28 Sep – 3 Oct 2026 · Wk 40
    2 weeks ago · 21 Sep – 26 Sep 2026 · Wk 39

  The option's value is the Monday as YYYY-MM-DD (UTC). The generate-report
  edge function re-derives the end of the week and the label from that date, so
  the two sides cannot drift.
*/
const DAY = 86400000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const iso = (d) => d.toISOString().slice(0, 10);
const short = (d) => `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;

// ISO-8601 week number of the week containing this (UTC) date.
export function isoWeekNumber(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d - yearStart) / DAY + 1) / 7);
}

export function mondayOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
}

export function weekLabel(monday, now = new Date()) {
  const sat = new Date(monday.getTime() + 5 * DAY);
  const thisMonday = mondayOf(now);
  const weeksAgo = Math.round((thisMonday - monday) / (7 * DAY));
  const rel = weeksAgo === 0 ? "This week" : weeksAgo === 1 ? "Last week" : `${weeksAgo} weeks ago`;
  return `${rel} · ${short(monday)} – ${short(sat)} ${sat.getUTCFullYear()} · Wk ${isoWeekNumber(monday)}`;
}

// "5 Oct – 10 Oct 2026 · Wk 41" — the week on its own, no "Last week" prefix.
export function weekRange(monday) {
  const sat = new Date(monday.getTime() + 5 * DAY);
  return `${short(monday)} – ${short(sat)} ${sat.getUTCFullYear()} · Wk ${isoWeekNumber(monday)}`;
}

/*
  The calendar's rule: picking a day reports the Monday-to-Saturday week BEFORE
  the next Monday on or after it. So picking a Monday gives the week just ended
  (the Digest's rhythm: prepared Monday, covering last week), and picking any
  day inside a week gives that week.
*/
export function coveredMonday(day) {
  const d = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate()));
  const nextMonday = new Date(d.getTime() + ((8 - (d.getUTCDay() || 7)) % 7) * DAY);
  return new Date(nextMonday.getTime() - 7 * DAY);
}

// A week can be reported once its Saturday has arrived.
export function canReportWeek(monday, now = new Date()) {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return monday.getTime() + 5 * DAY <= today;
}

export const toIsoDay = iso;

// "" (no week) means the report describes the project/person as it stands now.
export const NO_WEEK = "all";

export function buildWeekOptions(count = 12, now = new Date()) {
  const thisMonday = mondayOf(now);
  const options = [{ value: NO_WEEK, label: "Overall — current status" }];
  for (let i = 0; i < count; i++) {
    const monday = new Date(thisMonday.getTime() - i * 7 * DAY);
    options.push({ value: iso(monday), label: weekLabel(monday, now) });
  }
  return options;
}
